import { SubscriptionInterval } from '../../__generated__/globalTypes';

/** Which checkout screen is showing; `card` is the fallback rail's entry. */
export type CheckoutScreen = 'account' | 'card' | 'plan' | 'authorize';

/** How the subscription will be funded. */
export type CheckoutRail = 'ach' | 'card';

export interface ICheckoutState {
  screen: CheckoutScreen;
  rail: CheckoutRail;
  selectedAccountId: string | null;
  selectedInterval: SubscriptionInterval;
  thirdPartyAck: boolean;
  /** The confirmed card setup the subscribe names; null until confirmed. */
  cardSetupIntentId: string | null;
}

export type CheckoutAction =
  | { type: 'selectAccount'; accountId: string }
  | { type: 'selectInterval'; interval: SubscriptionInterval }
  | { type: 'setThirdPartyAck'; acknowledged: boolean }
  | { type: 'payByCard' }
  | { type: 'cardConfirmed'; setupIntentId: string }
  | { type: 'advance' }
  | { type: 'back' }
  | { type: 'accountRemoved' };

/**
 * The state a fresh checkout opens with: the account screen, the annual
 * interval preselected, no acknowledgment, and the given account id (or none)
 * selected.
 */
export function initialCheckoutState(preselectedAccountId: string | null): ICheckoutState {
  return {
    screen: 'account',
    rail: 'ach',
    selectedAccountId: preselectedAccountId,
    // Annual is the pre-selected, discounted option.
    selectedInterval: SubscriptionInterval.Annual,
    thirdPartyAck: false,
    cardSetupIntentId: null,
  };
}

function forward(state: ICheckoutState): CheckoutScreen {
  switch (state.screen) {
    case 'account':
      return 'plan';
    case 'card':
      // The card screen advances through `cardConfirmed`, never blindly: the
      // setup id the next screens depend on only exists once Stripe confirms.
      return 'card';
    case 'plan':
      return 'authorize';
    case 'authorize':
    default:
      return 'authorize';
  }
}

function backward(state: ICheckoutState): CheckoutScreen {
  switch (state.screen) {
    case 'account':
      return 'account';
    case 'card':
      return 'account';
    case 'plan':
      // The card rail rejoined here, so back retraces into it.
      return state.rail === 'card' ? 'card' : 'account';
    case 'authorize':
    default:
      return 'plan';
  }
}

/**
 * Moves checkout between its screens, on either rail.
 *
 * Changing the account clears the acknowledgment. The disclosure names the
 * account it is about — it interpolates the mask — so carrying a `true` across
 * a change would store evidence claiming the subscriber acknowledged a
 * disclosure for an account they were never shown one for.
 *
 * Advancing from the account screen re-commits to the bank rail: a subscriber
 * who visited the card screen and came back is choosing to debit, and a stale
 * card setup id must not survive into that subscribe.
 */
export function checkoutReducer(state: ICheckoutState, action: CheckoutAction): ICheckoutState {
  switch (action.type) {
    case 'selectAccount':
      if (action.accountId === state.selectedAccountId) {
        return state;
      }
      return { ...state, selectedAccountId: action.accountId, thirdPartyAck: false };
    case 'selectInterval':
      return { ...state, selectedInterval: action.interval };
    case 'setThirdPartyAck':
      return { ...state, thirdPartyAck: action.acknowledged };
    case 'payByCard':
      return { ...state, screen: 'card', rail: 'card' };
    case 'cardConfirmed':
      return { ...state, screen: 'plan', cardSetupIntentId: action.setupIntentId };
    case 'advance':
      if (state.screen === 'account') {
        return { ...state, screen: forward(state), rail: 'ach', cardSetupIntentId: null };
      }
      return { ...state, screen: forward(state) };
    case 'back':
      return { ...state, screen: backward(state) };
    case 'accountRemoved':
      // The selected account left the eligible list (a refetch dropped it) —
      // snap to the account screen rather than stranding a later screen with
      // nothing to render. The stale ack goes with the stale selection. The
      // card rail never depends on the account, so it only sheds the stale
      // selection and stays where it is.
      return {
        ...state,
        screen: state.rail === 'card' ? state.screen : 'account',
        selectedAccountId: null,
        thirdPartyAck: false,
      };
    default:
      return state;
  }
}
